import { useCallback } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import { type RecaptchaResult } from '@atlassian/google-recaptcha';

import { type ExternalUser, type User } from '@atlaskit/user-picker';

import {
	getInstanceUrl,
	getLandingPathByProduct,
	isAcceptableFailure,
	isAcceptableFailureForUserInvite,
	shouldReportFailedInvite,
	chunk,
} from '../../utils';
import {
	type BulkInviteResponse,
	type BulkInviteFailureResponse,
	type InvitedUser,
	type Flag,
	type BulkInviteSuccessResponse,
	type InvitedUserResult,
	type InviteApiFailureResponse,
	isUserWithEmail,
	type InviteProcessingResponse,
	InviteProcessingResponseStatus,
	EnablementTouchpoint,
} from '../../types';
import { defaultInviteApiClient, INVITE_API_MAX_USERS } from '../../clients';
import {
	inviteFormSubmitted,
	inviteFormSubmittedTimings,
	parallelInviteRequestSubmitted,
} from '../../analytics/ufoExperiences';
import { useInvitePeopleFlags } from '../Flags/InvitePeopleFlag';

import { useInvitePeopleContext } from './InvitePeopleContext';
import { useInviteeListStateContext } from '../InviteeList';
import { useProducts } from '../ProductSelect';
import { type RecaptchaOptions, getErrorDetails } from '../../clients/helpers';
import { useAnalytics } from '../../analytics';
import { InvitePeopleAnalytics } from './InvitePeopleAnalytics';
import { useExperimentalFeatures } from '../../experiments/experimentalFeatures';
import { type OptionType } from '../InviteeList/InviteeListContext';

import { fg } from '@atlaskit/platform-feature-flags';

export type InviteUsersHookProps = {
	continueUrl?: string;
	onInviteCompletedHandler?: (inviteResponse: BulkInviteResponse) => void;
	onReopenHandler?: () => void;
};

interface HandleInviteFailureArgs {
	result: BulkInviteFailureResponse;
	flags: Flag[];
}

interface FireSuccessAnalyticsArgs {
	result: BulkInviteSuccessResponse;
	usersWithOrigin: { user: InvitedUser; origin: OriginTracer }[];
	userPickerOptions: OptionType[];
	inviteFromSuccessModal?: boolean;
}

interface HandleInviteSuccessArgs {
	result: BulkInviteSuccessResponse;
	usersWithOrigin: { user: InvitedUser; origin: OriginTracer }[];
	flags: Flag[];
	userPickerOptions: OptionType[];
	inviteFromSuccessModal?: boolean;
}

const useInviteUsers = ({
	continueUrl,
	onInviteCompletedHandler,
	onReopenHandler,
}: InviteUsersHookProps) => {
	const analytics = useAnalytics(InvitePeopleAnalytics);
	const { cloudId, productId, source, jiraSubProduct, experimental_productContext } =
		useInvitePeopleContext();
	const { selectedProducts, hasPreselectedTwc } = useProducts();
	const { users, getExternalUserType, userPickerOptions } = useInviteeListStateContext();
	const instanceUrl = getInstanceUrl();
	const inviteUsersFlags = useInvitePeopleFlags(onReopenHandler);
	const { confluenceXflowOnboarding, invitePromptOnLinkV3 } = useExperimentalFeatures();

	const fireInviteSuccessAnalytics = useCallback(
		({
			result,
			usersWithOrigin,
			userPickerOptions,
			inviteFromSuccessModal,
		}: FireSuccessAnalyticsArgs) => {
			const { invited, accessRequested } = result;

			const getOption = (user: InvitedUser) =>
				userPickerOptions?.find(
					(option) =>
						(option.type === 'user' || option.type === 'external_user') &&
						(option as User).email === user.email &&
						(option as User).isExternal,
				);

			invited.forEach((user: InvitedUser & InvitedUserResult) => {
				const origin = usersWithOrigin.find(
					(userWithOrigin) =>
						userWithOrigin.user.id === user.id || userWithOrigin.user.email === user.email,
				)?.origin;
				const originAttributes = origin?.toAnalyticsAttributes({
					hasGeneratedId: true,
				});

				const option = getOption(user);
				const attributes = {
					originAttributes,
					userId: user.id!,
					products: selectedProducts,
					inviteResult: user.results,
					externalUserType: getExternalUserType(user),
					isUserEmailVisible: isUserWithEmail(user),
					isAioliv3Enabled: invitePromptOnLinkV3,
					inviteFromSuccessModal,
					hasPreselectedTwc,
					...(option ? { sources: (option as ExternalUser).sources } : {}),
				};
				analytics.userInvited(attributes);
			});

			accessRequested.forEach((user: InvitedUser & InvitedUserResult) => {
				const origin = usersWithOrigin.find(
					(userWithOrigin) =>
						userWithOrigin.user.id === user.id || userWithOrigin.user.email === user.email,
				)?.origin;
				const originAttributes = origin?.toAnalyticsAttributes({
					hasGeneratedId: true,
				});

				const option = getOption(user);
				const attributes = {
					originAttributes,
					userId: user.id!,
					products: selectedProducts,
					inviteResult: user.results,
					externalUserType: getExternalUserType(user),
					isUserEmailVisible: isUserWithEmail(user),
					isAioliv3Enabled: invitePromptOnLinkV3,
					hasPreselectedTwc,
					...(option ? { sources: (option as ExternalUser).sources } : {}),
				};
				analytics.userAccessRequested(attributes);
			});
		},
		[selectedProducts, getExternalUserType, analytics, invitePromptOnLinkV3, hasPreselectedTwc],
	);

	const handleInviteFailureResponse = useCallback(
		({ result, flags }: HandleInviteFailureArgs) => {
			const { failure } = result;
			const { message, code, status } = failure;
			const inviteFormSubmittedConfig = {
				metadata: {
					inviteeCount: users.length,
					productCount: selectedProducts.length,
				},
			};

			// There should not be a failure SLO event if the error is a user network failure error
			if (!isAcceptableFailureForUserInvite(failure)) {
				if (shouldReportFailedInvite(failure)) {
					inviteFormSubmitted.failure(inviteFormSubmittedConfig);
					analytics.inviteFailedSLO(invitePromptOnLinkV3, code, message, status);
					if (invitePromptOnLinkV3) {
						parallelInviteRequestSubmitted.failure();
					}
				} else {
					inviteFormSubmitted.success(inviteFormSubmittedConfig);
					analytics.inviteSucceededSLO(invitePromptOnLinkV3);
					if (invitePromptOnLinkV3) {
						parallelInviteRequestSubmitted.success();
					}
				}
			}

			const failureFlag = inviteUsersFlags.failure(result);

			onInviteCompletedHandler?.(result);

			if (failureFlag) {
				flags.push(failureFlag);
			}
		},
		[
			users.length,
			selectedProducts.length,
			inviteUsersFlags,
			onInviteCompletedHandler,
			analytics,
			invitePromptOnLinkV3,
		],
	);

	const handleInviteSuccessResponse = useCallback(
		({
			result,
			usersWithOrigin,
			flags,
			userPickerOptions,
			inviteFromSuccessModal,
		}: HandleInviteSuccessArgs) => {
			onInviteCompletedHandler?.(result);

			analytics.inviteSucceededSLO(invitePromptOnLinkV3);
			inviteFormSubmitted.success({
				metadata: {
					inviteeCount: result.invited.length + result.accessRequested.length,
					productCount: selectedProducts.length,
				},
			});
			if (invitePromptOnLinkV3) {
				parallelInviteRequestSubmitted.success();
			}

			fireInviteSuccessAnalytics({
				result,
				usersWithOrigin,
				userPickerOptions,
				inviteFromSuccessModal,
			});

			const successFlag = inviteUsersFlags.success(result);
			if (successFlag) {
				flags.push(successFlag);
			}

			const errorFlag = inviteUsersFlags.error(result);
			if (errorFlag) {
				flags.push(errorFlag);
			}
		},
		[
			onInviteCompletedHandler,
			analytics,
			selectedProducts.length,
			fireInviteSuccessAnalytics,
			inviteUsersFlags,
			invitePromptOnLinkV3,
		],
	);

	const getContinueUrlWithOrigin = useCallback(
		({
			continueUrl,
			origin,
			source,
		}: {
			continueUrl: string | undefined;
			origin: OriginTracer;
			source: string;
		}) => {
			let productContinueUrl = continueUrl;

			if (!productContinueUrl) {
				productContinueUrl =
					selectedProducts.length === 1
						? `${instanceUrl}${getLandingPathByProduct(selectedProducts[0])}`
						: instanceUrl;
			}

			// Also try to find urls that forward newly invited users to jira setting pages and redirect them to a product landing page instead.
			// We should do this so newly invited users are not seeing a permission denied screen when accessing restricted pages.
			if (productContinueUrl.match(/.*\/jira.*\/settings/gim)) {
				productContinueUrl =
					selectedProducts.length === 1
						? `${instanceUrl}${getLandingPathByProduct(selectedProducts[0])}`
						: instanceUrl;
			}

			const productContinueUrlWithOrigin = origin.addToUrl(productContinueUrl);
			return `${productContinueUrlWithOrigin}&source=${source}`;
		},
		[instanceUrl, selectedProducts],
	);

	const inviteUsers = useCallback(
		async (
			getRecaptchaToken?: RecaptchaResult['getRecaptchaToken'],
			inviteFromSuccessModal?: boolean,
		): Promise<InviteProcessingResponse<BulkInviteResponse, unknown>> => {
			let productContinueUrl = continueUrl;

			if (!productContinueUrl) {
				productContinueUrl =
					selectedProducts.length === 1
						? `${instanceUrl}${getLandingPathByProduct(selectedProducts[0])}`
						: instanceUrl;
			}

			// Also try to find urls that forward newly invited users to jira setting pages and redirect them to a product landing page instead.
			// We should do this so newly invited users are not seeing a permission denied screen when accessing restricted pages.
			if (productContinueUrl.match(/.*\/jira.*\/settings/gim)) {
				productContinueUrl =
					selectedProducts.length === 1
						? `${instanceUrl}${getLandingPathByProduct(selectedProducts[0])}`
						: instanceUrl;
			}

			const resources = selectedProducts.map(
				(selectedProduct) => `ari:cloud:${selectedProduct}::site/${cloudId}`,
			);

			const inviteRequest = async (usersToInvite: InvitedUser[]): Promise<BulkInviteResponse> => {
				inviteFormSubmitted.mark(inviteFormSubmittedTimings.getRecaptchaToken.startMark);

				const recaptchaToken = await getRecaptchaToken?.('v1_invitations_invite').catch(
					// If token generation fails we want to just continue with the
					// request without a recaptcha token (the lib will fire an event)
					() => undefined,
				);

				inviteFormSubmitted.mark(inviteFormSubmittedTimings.getRecaptchaToken.endMark);

				const recaptchaAction = `${productId}-${source}`;

				const recaptchaOptions: RecaptchaOptions = {
					recaptchaToken,
					recaptchaAction,
				};

				try {
					let result: BulkInviteResponse;
					if (fg('hot-123886-revert-v3-invite-endpoint')) {
						result = await defaultInviteApiClient.inviteUsers(
							{
								continueUrl: usersToInvite[0].continueUrl,
								users: usersToInvite.map(({ continueUrl: _continueUrl, ...user }) => user),
								resources,
								suppressInviteEmail: false,
								metadata: {
									sourceProduct: jiraSubProduct ?? productId,
									touchpoint: EnablementTouchpoint.CORE_INVITES,
									...(experimental_productContext && {
										productContext: experimental_productContext,
									}),
								},
							},
							recaptchaOptions,
						);
					} else {
						result = await defaultInviteApiClient.inviteUsersV3(
							{
								users: usersToInvite,
								resources,
								suppressInviteEmail: false,
								metadata: {
									sourceProduct: jiraSubProduct ?? productId,
									touchpoint: EnablementTouchpoint.CORE_INVITES,
									...(experimental_productContext && {
										productContext: experimental_productContext,
									}),
								},
							},
							confluenceXflowOnboarding ? undefined : recaptchaOptions,
						);
					}

					if ('failure' in result) {
						const { message, code, status } = result.failure;
						analytics.inviteRequestFailed(
							selectedProducts,
							!!recaptchaToken,
							code,
							message,
							status,
							inviteFromSuccessModal,
						);
					} else {
						const { invited, accessRequested, error } = result;
						analytics.inviteRequestSucceeded(
							selectedProducts,
							{
								invited: invited.map((u) => u.id!),
								accessRequested: accessRequested.map((u) => u.id!),
								error: error.map(({ error }) => error as InviteApiFailureResponse),
							},
							!!recaptchaToken,
							inviteFromSuccessModal,
						);
					}
					return result;
				} catch (err) {
					const { code, message, status } = getErrorDetails(err);
					analytics.inviteRequestFailed(
						selectedProducts,
						!!recaptchaToken,
						code,
						message,
						status,
						inviteFromSuccessModal,
					);
					throw err;
				}
			};

			const inviteRequestParallel = async (): Promise<BulkInviteResponse> => {
				const result: BulkInviteResponse = {
					invited: [],
					accessRequested: [],
					error: [],
				};

				const userBatches = chunk(users, INVITE_API_MAX_USERS);
				const responses = await Promise.allSettled(
					userBatches.map(async (userBatch) => {
						try {
							const response = await inviteRequest(userBatch);
							if ('failure' in response) {
								for (const user of userBatch) {
									result.error.push({ ...user, error: response.failure });
								}
							} else {
								result.invited.push(...response.invited);
								result.accessRequested.push(...response.accessRequested);
								result.error.push(...response.error);
							}
							return response;
						} catch (err: any) {
							for (const user of userBatch) {
								result.error.push({
									...user,
									error: {
										code: err.code,
										message: err.message || String(err),
									},
								});
							}
							throw err;
						}
					}),
				);

				const rejectedResponse = responses.find(
					(response): response is PromiseRejectedResult => response.status === 'rejected',
				);
				const successResponse = responses.find(
					(response): response is PromiseFulfilledResult<BulkInviteSuccessResponse> =>
						response.status === 'fulfilled' && !('failure' in response.value),
				);
				const failureResponse = responses.find(
					(response): response is PromiseFulfilledResult<BulkInviteFailureResponse> =>
						response.status === 'fulfilled' && 'failure' in response.value,
				);

				if (rejectedResponse && !successResponse && !failureResponse) {
					throw rejectedResponse.reason;
				}
				if (!successResponse && failureResponse) {
					return failureResponse.value;
				}
				return result;
			};

			try {
				inviteFormSubmitted.mark(inviteFormSubmittedTimings.inviteUsersPromise.startMark);

				if (invitePromptOnLinkV3) {
					parallelInviteRequestSubmitted.start();
				}

				const usersWithOrigin = users.map((user) => {
					const origin = new OriginTracer({ product: productId });
					const continueUrlWithOrigin = getContinueUrlWithOrigin({
						continueUrl,
						origin,
						source,
					});
					return {
						user: {
							...user,
							continueUrl: continueUrlWithOrigin,
						},
						origin,
					};
				});

				const usersToInvite = usersWithOrigin.map(({ user }) => user);

				const result = invitePromptOnLinkV3
					? await inviteRequestParallel()
					: await inviteRequest(usersToInvite);

				inviteFormSubmitted.mark(inviteFormSubmittedTimings.inviteUsersPromise.endMark);

				const flags: Flag[] = [];

				if ('failure' in result) {
					handleInviteFailureResponse({
						result,
						flags,
					});
				} else {
					handleInviteSuccessResponse({
						result,
						usersWithOrigin,
						flags,
						userPickerOptions,
						inviteFromSuccessModal,
					});
				}
				return {
					responseStatus: InviteProcessingResponseStatus.SUCCESS,
					response: result,
					flags: flags,
				};
			} catch (err: unknown) {
				inviteFormSubmitted.mark(inviteFormSubmittedTimings.inviteUsersPromise.endMark);

				const { code, message, status } = getErrorDetails(err);

				if (!isAcceptableFailure(message, status)) {
					analytics.inviteFailedSLO(invitePromptOnLinkV3, code, message, status);
				}

				// TODO: add metadata indicating failure, migrate off custom SLO event to using UFO only
				inviteFormSubmitted.failure({
					metadata: {
						inviteeCount: users.length,
						productCount: selectedProducts.length,
					},
				});

				if (invitePromptOnLinkV3) {
					parallelInviteRequestSubmitted.failure();
				}

				return {
					responseStatus: InviteProcessingResponseStatus.ERROR,
					error: err,
					flags: [inviteUsersFlags.genericError()],
				};
			}
		},
		[
			continueUrl,
			productId,
			jiraSubProduct,
			selectedProducts,
			instanceUrl,
			cloudId,
			analytics,
			users,
			handleInviteFailureResponse,
			handleInviteSuccessResponse,
			inviteUsersFlags,
			confluenceXflowOnboarding,
			userPickerOptions,
			invitePromptOnLinkV3,
			source,
			experimental_productContext,
			getContinueUrlWithOrigin,
		],
	);
	return {
		inviteUsers,
	};
};

export default useInviteUsers;
